/**
 * Pure calendar-grid and business-date helpers for the campaign scheduling
 * calendar (docs/features/campaign-scheduling/01_product_brief.md).
 *
 * "Send date" is the real Mailchimp send date (what a marketer sets and
 * thinks in). "Activation date" is CIS's own internal date — when it pushes
 * the audience to Mailchimp — derived from the send date, never set
 * directly. Both terms are load-bearing UI vocabulary, agreed directly with
 * the requesting user; keep them distinct everywhere.
 */

import type { CadenceChannel } from "../data/api-types";
import { formatDateOrDash } from "./formatters";

/** ISO date string, "YYYY-MM-DD" — date-only, no time-of-day. CIS doesn't
 * send the email; Mailchimp does, at whatever time it's configured there. */
export type IsoDate = string;

function parseIsoDate(date: IsoDate): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function toIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

/** CIS's activation date for a given send date: our working day before it —
 * the lead-time rule confirmed directly with the requesting user — skipping
 * weekends and the weekdays we do not work (`closedDays`: bank holidays and
 * shutdowns, served by the backend from Time with the auto-activation state,
 * d88), exactly as the backend's own rule does. */
export function activationDateFor(
  sendDate: IsoDate,
  closedDays: readonly IsoDate[] = [],
): IsoDate {
  const d = parseIsoDate(sendDate);
  do {
    d.setUTCDate(d.getUTCDate() - 1);
  } while (
    d.getUTCDay() === 0 ||
    d.getUTCDay() === 6 ||
    closedDays.includes(toIsoDate(d))
  );
  return toIsoDate(d);
}

/** The sentence telling a marketer when a send dated `sendDate` activates.
 * It promises "CIS will activate" only while the daily auto-activate job is
 * on (`autoActivationEnabled`, from jobctl): paused, nothing activates it but
 * a person. `null` (state not loaded, or unreadable) promises neither.
 *
 * A mailing or call round is activated from its campaign ("Mark ready now",
 * "Open now"), and never pushes anything to Mailchimp. */
export function activationHint(
  sendDate: IsoDate,
  autoActivationEnabled: boolean | null,
  subject: "this" | "it" = "this",
  channel: CadenceChannel = "email",
  closedDays: readonly IsoDate[] = [],
): string {
  const on = formatDateOrDash(activationDateFor(sendDate, closedDays));
  if (channel === "direct_mail" || channel === "call") {
    const byHand =
      channel === "direct_mail"
        ? `mark ${subject} ready from its campaign when you're about to pack it`
        : `open ${subject} from its campaign when you're ready to start calling`;
    if (autoActivationEnabled === true) {
      return channel === "direct_mail"
        ? `One will mark ${subject} ready — fixing who it goes to — on ${on}, the working day before, unless you do it sooner.`
        : `One will open ${subject} — raising its call tickets — on ${on}, the working day before, unless you do it sooner.`;
    }
    const sentence = `${byHand.charAt(0).toUpperCase()}${byHand.slice(1)}.`;
    return autoActivationEnabled === false
      ? `Automatic activation is off — ${byHand}.`
      : sentence;
  }
  if (autoActivationEnabled === true) {
    return `One will activate ${subject} — pushing the audience to Mailchimp — on ${on}, the working day before.`;
  }
  if (autoActivationEnabled === false) {
    return `Automatic activation is off — activate ${subject} manually on ${on}, the working day before.`;
  }
  return `Its activation date — when the audience goes to Mailchimp — is ${on}, the working day before.`;
}

export interface CalendarDay {
  date: IsoDate;
  /** Falls outside the displayed month (leading/trailing days shown to
   * complete the grid's weeks). */
  outsideMonth: boolean;
  isToday: boolean;
}

/** A full 6-week (42-day) grid for the given month, starting on Monday —
 * the standard UK business-calendar week start. Always 6 weeks so a
 * month's grid height never jumps as the user navigates between months. */
export function monthGrid(
  year: number,
  month: number, // 1-based (1 = January), matching how a marketer says "October"
  today: IsoDate,
): CalendarDay[] {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const firstWeekday = (firstOfMonth.getUTCDay() + 6) % 7; // Monday = 0
  const gridStart = new Date(firstOfMonth);
  gridStart.setUTCDate(gridStart.getUTCDate() - firstWeekday);

  const days: CalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    const iso = toIsoDate(d);
    days.push({
      date: iso,
      outsideMonth: d.getUTCMonth() !== month - 1,
      isToday: iso === today,
    });
  }
  return days;
}

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Previous/next month, wrapping the year. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const total = year * 12 + (month - 1) + delta;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}
